import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMockPortal } from '../mock/useMockPortal';
import { formatPortalCurrency, setPendingPayment } from '../mock/store';
import type { PaymentMethod } from '../mock/types';
import { elfPrimaryButtonSx } from '../elfTheme';

const LoanPay: React.FC = () => {
  const navigate = useNavigate();
  const { loan } = useMockPortal();
  const [amountMode, setAmountMode] = useState<'installment' | 'custom'>(
    'installment'
  );
  const [customAmount, setCustomAmount] = useState(
    String(loan.nextPaymentAmount)
  );
  const [method, setMethod] = useState<PaymentMethod>('ach');

  const amount =
    amountMode === 'installment'
      ? loan.nextPaymentAmount
      : Number.parseFloat(customAmount) || 0;

  const handleContinue = () => {
    if (amount <= 0) return;
    setPendingPayment({ amount, method, amountMode });
    navigate('/portal/loans/pay/confirm');
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Make a payment
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Preview only — no funds will be transferred.
      </Typography>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <FormControl sx={{ mb: 3, display: 'block' }}>
          <FormLabel>Payment amount</FormLabel>
          <RadioGroup
            value={amountMode}
            onChange={(_, v) => setAmountMode(v as 'installment' | 'custom')}
          >
            <FormControlLabel
              value="installment"
              control={<Radio />}
              label={`Scheduled installment (${formatPortalCurrency(loan.nextPaymentAmount)})`}
            />
            <FormControlLabel
              value="custom"
              control={<Radio />}
              label="Custom amount"
            />
          </RadioGroup>
          {amountMode === 'custom' && (
            <TextField
              label="Amount"
              type="number"
              size="small"
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              inputProps={{ min: 1, step: '0.01' }}
              sx={{ mt: 1, maxWidth: 200 }}
            />
          )}
        </FormControl>

        <FormControl sx={{ mb: 3, display: 'block' }}>
          <FormLabel>Payment method</FormLabel>
          <RadioGroup
            value={method}
            onChange={(_, v) => setMethod(v as PaymentMethod)}
          >
            <FormControlLabel
              value="ach"
              control={<Radio />}
              label="Bank account (ACH) — sample"
            />
            <FormControlLabel
              value="card"
              control={<Radio />}
              label="Debit / credit card — sample"
            />
          </RadioGroup>
        </FormControl>

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            onClick={handleContinue}
            disabled={amount <= 0}
            sx={elfPrimaryButtonSx}
          >
            Continue
          </Button>
          <Button onClick={() => navigate('/portal/loans')}>Cancel</Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default LoanPay;
